<template>
  <div class="sp-skeleton-loader" :class="classModifiers" :style="style" part="base" v-bind="a11yAttrs">
    <div v-if="loading" class="sp-skeleton-loader__skeletons" aria-hidden="true">
      <div
        v-for="skeleton in renderSkeletons"
        :key="skeleton.key"
        class="sp-skeleton-loader__skeleton"
        :class="skeleton.skeletonClass"
      >
        <div
          v-for="bone in skeleton.bones"
          :key="bone.key"
          class="sp-skeleton-loader__bone"
          part="bone"
          :class="bone.className"
        ></div>
      </div>
    </div>

    <div v-else class="sp-skeleton-loader__content" part="content">
      <slot />
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { toBoolean, toCssUnit, trimToUndefined } from "#ce/utils/props";
import { props as skeletonLoaderProps } from "./props";
import { resolveSkeletonRenderItems } from "./types";

const props = defineProps(skeletonLoaderProps);
const isBoilerplate = computed(() => toBoolean(props.boilerplate));
const loading = computed(() => toBoolean(props.loading));

// Parse `type` string (e.g. "text@3", "heading, paragraph, button")
// into skeleton items + bone classes.
const renderSkeletons = computed(() =>
  resolveSkeletonRenderItems(props.type).map((item, itemIndex) => ({
    key: `${item.type}-${itemIndex}`,
    skeletonClass: `--skeleton-${item.type}`,
    bones: item.bones.map((boneClass, boneIndex) => ({
      key: `${item.type}-${itemIndex}-${boneIndex}`,
      className: boneClass,
    })),
  })),
);

const a11yAttrs = computed(() => {
  if (isBoilerplate.value || !loading.value) {
    return {};
  }
  return {
    "aria-busy": "true",
    "aria-live": "polite",
    "aria-label": props.loadingText,
    role: "status",
  };
});

// Prevent empty values from being converted to 0rem.
function toOptionalDimension(value) {
  const normalized = trimToUndefined(value);

  if (normalized === undefined || normalized === null) {
    return undefined;
  }

  return toCssUnit(normalized);
}

const style = computed(() => ({
  "--width": toOptionalDimension(props.width),
  "--height": toOptionalDimension(props.height),
  "--min-width": toOptionalDimension(props.minWidth),
  "--min-height": toOptionalDimension(props.minHeight),
  "--max-width": toOptionalDimension(props.maxWidth),
  "--max-height": toOptionalDimension(props.maxHeight),
}));

const classModifiers = computed(() => ({
  "--boilerplate": isBoilerplate.value,
  "--animation-pulse": props.animation === "pulse",
  [`--elevation-${props.elevation}`]: props.elevation,
}));
</script>

<style>
:host {
  /* Surface */
  --color: var(--sp-comp-skeleton-color, var(--sp-sys-color-outline));
  --border: var(--sp-comp-skeleton-border, 0 solid transparent);
  --border-radius: var(--sp-comp-skeleton-border-radius, var(--sp-ref-border-radius-xs));

  /* Motion */
  --animation-duration: var(--sp-comp-skeleton-animation-duration, 1.5s);
  --wave-color: var(--sp-comp-skeleton-wave-color, rgba(255, 255, 255, 0.35));

  /* Layout */
  --gap: var(--sp-comp-skeleton-gap, var(--sp-ref-scale-8));
  --padding-block: var(--sp-comp-skeleton-padding-block, 0);
  --padding-inline: var(--sp-comp-skeleton-padding-inline, 0);
  --bone-gap: var(--sp-comp-skeleton-bone-gap, var(--sp-ref-scale-4));

  /* Text and heading */
  --text-height: var(--sp-comp-skeleton-text-height, var(--sp-ref-scale-12));
  --text-border-radius: var(--sp-comp-skeleton-text-border-radius, var(--sp-ref-border-radius-xs));
  --text-2-max-width: var(--sp-comp-skeleton-text-2-max-width, 85%);
  --text-3-max-width: var(--sp-comp-skeleton-text-3-max-width, 65%);
  --heading-height: var(--sp-comp-skeleton-heading-height, var(--sp-ref-scale-24));
  --heading-max-width: var(--sp-comp-skeleton-heading-max-width, 45%);
  --heading-border-radius: var(--sp-comp-skeleton-heading-border-radius, var(--sp-ref-border-radius-md));

  /* Image and avatar */
  --image-height: var(--sp-comp-skeleton-image-height, 9.375rem);
  --image-border-radius: var(--sp-comp-skeleton-image-border-radius, var(--border-radius));
  --avatar-size: var(--sp-comp-skeleton-avatar-size, var(--sp-sys-icon-size-large, var(--sp-ref-scale-40)));

  /* Chip */
  --chip-width: var(--sp-comp-skeleton-chip-width, var(--sp-ref-scale-96));
  --chip-height: var(
    --sp-comp-skeleton-chip-height,
    var(--sp-sys-form-field-compact-min-height, var(--sp-ref-scale-32))
  );
  --chip-border-radius: var(--sp-comp-skeleton-chip-border-radius, var(--sp-ref-border-radius-lg));

  /* Button */
  --button-width: var(--sp-comp-skeleton-button-width, 9rem);
  --button-height: var(
    --sp-comp-skeleton-button-height,
    var(--sp-comp-button-size, var(--sp-sys-form-field-min-height, var(--sp-ref-scale-48)))
  );
  --button-border-radius: var(
    --sp-comp-skeleton-button-border-radius,
    var(--sp-comp-button-border-radius, var(--border-radius))
  );

  /* Divider */
  --divider-height: var(--sp-comp-skeleton-divider-height, var(--sp-sys-form-field-border-width, 2px));
  --divider-border-radius: var(--sp-comp-skeleton-divider-border-radius, calc(var(--divider-height) / 2));

  display: block;
}
</style>

<style scoped lang="scss">
.sp-skeleton-loader {
  width: var(--width, auto);
  height: var(--height, auto);
  min-width: var(--min-width, 0px);
  min-height: var(--min-height, 0px);
  max-width: var(--max-width, none);
  max-height: var(--max-height, none);
  border-radius: var(--border-radius);
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  &.--elevation-1 {
    box-shadow: var(--sp-sys-elevation-1);
  }

  &.--elevation-2 {
    box-shadow: var(--sp-sys-elevation-2);
  }

  &.--elevation-3 {
    box-shadow: var(--sp-sys-elevation-3);
  }
}

// Skeleton stack container
.sp-skeleton-loader__skeletons {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: var(--gap);
  padding-block: var(--padding-block);
  padding-inline: var(--padding-inline);
  box-sizing: border-box;
  overflow: hidden;
}

// Individual skeleton item container
.sp-skeleton-loader__skeleton {
  display: flex;
  flex-direction: column;
  gap: var(--bone-gap);
  width: 100%;

  &.--skeleton-avatar,
  &.--skeleton-chip,
  &.--skeleton-button {
    width: max-content;
    min-width: 0;
  }

  &.--skeleton-image {
    flex: 1;
  }

  &.--skeleton-divider {
    gap: 0;
  }
}

// Bone base styles
.sp-skeleton-loader__bone {
  --bone-width: 100%;
  --bone-height: auto;
  --bone-max-width: none;
  --bone-border-radius: var(--border-radius);

  flex: 0 0 auto;
  position: relative;
  overflow: hidden;
  background-color: var(--color);
  border: var(--border);
  display: block;
  width: var(--bone-width);
  height: var(--bone-height);
  max-width: var(--bone-max-width);
  border-radius: var(--bone-border-radius);
  min-width: 0;
  box-sizing: border-box;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    transform: translateX(-100%);
    background: linear-gradient(90deg, transparent, var(--wave-color), transparent);
    animation: sp-skeleton-wave var(--animation-duration) linear infinite;
  }

  // Animation variants
  .sp-skeleton-loader.--animation-pulse & {
    animation: sp-skeleton-pulse var(--animation-duration) ease-in-out infinite;

    &::after {
      display: none;
    }
  }

  .sp-skeleton-loader.--boilerplate & {
    animation: none;

    &::after {
      display: none;
    }
  }

  // Bone type shapes
  &.--type-text {
    --bone-height: var(--text-height);
    --bone-border-radius: var(--text-border-radius);
  }

  &.--type-heading {
    --bone-height: var(--heading-height);
    --bone-max-width: var(--heading-max-width);
    --bone-border-radius: var(--heading-border-radius);
  }

  &.--type-image {
    flex: 1 1 auto;
    min-height: var(--image-height);
    --bone-height: auto;
    --bone-border-radius: var(--image-border-radius);
  }

  &.--type-avatar {
    --bone-width: var(--avatar-size);
    --bone-height: var(--avatar-size);
    --bone-border-radius: 50%;
  }

  &.--type-chip {
    --bone-width: var(--chip-width);
    --bone-height: var(--chip-height);
    --bone-border-radius: var(--chip-border-radius);
  }

  &.--type-button {
    --bone-width: var(--button-width);
    --bone-height: var(--button-height);
    --bone-border-radius: var(--button-border-radius);
  }

  &.--type-divider {
    --bone-height: var(--divider-height);
    --bone-border-radius: var(--divider-border-radius);
  }

  // Narrow successive text bones to mimic a paragraph
  &.--type-text + &.--type-text {
    --bone-max-width: var(--text-2-max-width);
  }

  &.--type-text + &.--type-text + &.--type-text {
    --bone-max-width: var(--text-3-max-width);
  }
}

.sp-skeleton-loader__content {
  flex: 1;
  width: 100%;
  min-width: 0;
}

@keyframes sp-skeleton-wave {
  100% {
    transform: translateX(100%);
  }
}

@keyframes sp-skeleton-pulse {
  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.45;
  }
}
</style>
